import React, {useEffect} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import useAuth from '../features/auth/hooks/use-auth';
import AppButton from '../features/common/components/app-button';
import useVerifyEmailTokenHandler from "../features/auth/hooks/use-verify-email-token-handler";
import {useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {schemaVerifyEmail} from "../features/auth/constants/schema-verify-email";
import {AuthenticatedUser} from "../features/auth/models/interfaces/authenticated-user.interface";

const EmailVerificationPage: React.FC = () => {
    const {activateAccount, currentUser, loading, updateAuthenticationField, logout} = useAuth();
    const {resendCountdown, handleResendToken} = useVerifyEmailTokenHandler();
    const location = useLocation();
    const navigate = useNavigate();

    const methods = useForm<{ token: string }>({
        resolver: yupResolver(schemaVerifyEmail),
        mode: "onSubmit",
    });

    const handleVerifyEmail = async () => {
        const isValid = await methods.trigger();
        if (!isValid) return;

        const {token} = methods.getValues();
        try {
            await activateAccount(token);
            const updatedUser: AuthenticatedUser = {
                ...currentUser!,
                hasEmailVerified: true,
            }
            await updateAuthenticationField('user', updatedUser);
        } catch (error) {
            console.error("Email verification failed:", error);
        }
    };

    useEffect(() => {
        const codeFromUrl = new URLSearchParams(location.search).get("token");
        if (codeFromUrl) {
            methods.setValue("token", codeFromUrl, { shouldDirty: false, shouldTouch: false });
            methods.handleSubmit(handleVerifyEmail)();
            navigate(location.pathname, { replace: true });
        }
    }, [location.search]);

    const tokenFromUrl = new URLSearchParams(location.search).get("token");

    if (tokenFromUrl) {
        return (
            <div className="min-h-screen bg-[#DFE6E7] flex items-center justify-center p-4">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                    <p className="font-semibold">Weryfikacja emaila...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#DFE6E7] flex items-center justify-center p-4">
            <div className="max-w-md w-full">
                <div className="flex flex-col gap-8 bg-white rounded-[2rem] shadow-sm p-8 border border-gray-200">
                    <h2 className="text-2xl font-bold text-gray-900">Sprawdź swój email</h2>
                    <div className="w-full font-semibold text-lg leading-[22px]">
                        <p>
                            Wysłaliśmy wiadomość na <span className="text-link">{currentUser?.email}</span>.
                        </p>
                        <p>
                            Kliknij link potwierdzający w wiadomości, aby zakończyć rejestrację.
                        </p>
                    </div>
                    <div className="flex justify-between">
                        <AppButton
                            onClick={handleResendToken}
                            type='submit'
                            disabled={loading}
                            loaderColor='var(--primary)'
                            className='!p-0 border-none underline ml-1 cursor-pointer transition hover:text-primary focus:text-primary'>
                            Wyślij ponownie {!!resendCountdown && `(${resendCountdown})`}
                        </AppButton>
                        <AppButton
                            onClick={logout}
                            type='button'
                            disabled={loading}
                            loaderColor='var(--primary)'
                            className='!p-0 border-none underline ml-1 cursor-pointer transition hover:text-primary focus:text-primary'>
                            Wyloguj
                        </AppButton>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EmailVerificationPage;
